import React, { useCallback, useEffect, useRef, useState } from 'react';

import { apiGet, apiPostAsUser, responseErrorMessage } from '../../../lib/api_v2';
import { startDesktopOauth } from '../../../lib/desktopOauth';
import { logError } from '../../../lib/logger';
import { cn } from '../../../lib/utils';

import ExternalLinkIcon from '../../../../assets/images/external-link.svg';
import SpinnerIcon from '../../../../assets/images/spinner.svg';

import { useRouteWorkspace } from '../../../contexts/RouteWorkspaceContext';
import useDesktopWindowFocusSignal from '../../../hooks/useDesktopWindowFocusSignal';
import { isSingleInstallTemplateInstalled, useWidgetTemplateActions } from '../../../hooks/useWidgetTemplateActions';
import useWorkspaceTemplateWidgetSignal from '../../../hooks/useWorkspaceTemplateWidgetSignal';
import IntegrationStatusBadge from '../../shared/IntegrationStatusBadge';
import SettingsButton from '../../shared/SettingsButton';

const EMPTY_TEMPLATES = Object.freeze([]);
const EMPTY_INTEGRATIONS = Object.freeze({});

function findTemplateElementBySlug(root, slug) {
  if (!root || !slug) return null;

  const elements = root.querySelectorAll('[data-widget-template-slug]');
  for (const element of elements) {
    if (element.dataset.widgetTemplateSlug === slug) return element;
  }

  return null;
}

const WidgetTemplateButton = React.memo(function WidgetTemplateButton({
  template,
  disabled,
  removeDisabled,
  isInstalling,
  isRemoving,
  wasInstalled,
  onInstall,
  onRemove,
}) {
  const isAlreadyInstalled = isSingleInstallTemplateInstalled(template);
  const handleInstallClick = useCallback(() => onInstall(template), [onInstall, template]);
  const handleRemoveClick = useCallback(() => onRemove(template), [onRemove, template]);

  if (isAlreadyInstalled) {
    return (
      <button
        type="button"
        className="flex min-h-10 w-full min-w-0 cursor-pointer items-center gap-3 rounded px-2 text-left text-sm leading-tight text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:text-neutral-400 dark:hover:bg-neutral-800"
        disabled={removeDisabled || !template.installed_widget_id}
        onClick={handleRemoveClick}
      >
        <span className="min-w-0 flex-1 truncate">{template.name}</span>
        {isRemoving ? <SpinnerIcon className="size-4 shrink-0 animate-spin text-gray-500 dark:text-neutral-400" /> : null}
        <span className="shrink-0 text-[11px] leading-none text-red-600 dark:text-red-400">{isRemoving ? 'Removing' : 'Remove'}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={cn(
        'flex min-h-10 w-full min-w-0 cursor-pointer items-center gap-3 rounded px-2 text-left text-sm leading-tight text-gray-900 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:text-neutral-100 dark:hover:bg-neutral-800',
        wasInstalled && 'text-gray-500 dark:text-neutral-400',
      )}
      disabled={disabled}
      onClick={handleInstallClick}
    >
      <span className="min-w-0 flex-1 truncate">{template.name}</span>
      {isInstalling ? (
        <SpinnerIcon className="size-4 shrink-0 animate-spin text-gray-500 dark:text-neutral-400" />
      ) : wasInstalled ? (
        <span className="shrink-0 text-[11px] leading-none text-gray-500 dark:text-neutral-400">Sent</span>
      ) : null}
    </button>
  );
});

const WidgetIntegrationRow = React.memo(function WidgetIntegrationRow({
  template,
  provider,
  status,
  disabled,
  removeDisabled,
  isInstalling,
  isRemoving,
  isAdmin,
  workspaceId,
  onInstall,
  onRemove,
  onError,
  desktopFocusSignal,
}) {
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const awaitingDesktopReturnRef = useRef(false);
  const configured = status?.configured === true;
  const connected = configured && Boolean(status?.connected);
  const isAlreadyInstalled = isSingleInstallTemplateInstalled(template);
  const handleInstallClick = useCallback(
    function handleIntegrationInstallClick() {
      if (!connected) return;
      onInstall(template);
    },
    [connected, onInstall, template],
  );
  const handleRemoveClick = useCallback(
    function handleIntegrationRemoveClick() {
      onRemove(template);
    },
    [onRemove, template],
  );
  const handleAuthorizeClick = useCallback(
    async function handleIntegrationAuthorizeClick() {
      if (!isAdmin || !workspaceId || !provider || isAuthorizing || !configured) return;

      setIsAuthorizing(true);
      onError(null);
      let desktopOauth = null;

      try {
        desktopOauth = await startDesktopOauth({ purpose: 'integration', provider, workspaceId });
        if (desktopOauth) {
          awaitingDesktopReturnRef.current = !desktopOauth.completed;
          return;
        }

        const response = await apiPostAsUser(`/workspaces/${workspaceId}/integrations/${provider}/authorize`, {
          client: 'web',
        });
        const data = await response.json();
        const authorizationUrl = data.data?.authorization_url;

        if (response.ok && data.ok === true && authorizationUrl) {
          window.location.href = authorizationUrl;
          return;
        }

        onError(`Unable to open ${template.name}.`);
        logError('WidgetGalleryView', 'Failed to authorize integration', data.errors);
      } catch (authorizeError) {
        onError(`Unable to open ${template.name}.`);
        logError('WidgetGalleryView', 'Failed to authorize integration', authorizeError);
      } finally {
        if (!desktopOauth || desktopOauth.completed) setIsAuthorizing(false);
      }
    },
    [configured, isAdmin, isAuthorizing, onError, provider, template.name, workspaceId],
  );

  useEffect(
    function reenableAuthorizeOnDesktopReturn() {
      // desktop OAuth hands off to the system browser and keeps the button
      // disabled; re-enable it once the shell window regains focus so the user
      // can retry after a cancelled or failed connect. Gate on the handoff ref:
      // a focus event landing while the authorize request is still in flight
      // must not unlock a duplicate flow.
      if (!awaitingDesktopReturnRef.current) return;

      awaitingDesktopReturnRef.current = false;
      setIsAuthorizing(false);
    },
    [desktopFocusSignal],
  );

  return (
    <div className="flex min-h-14 w-full min-w-0 items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-left dark:border-neutral-800 dark:bg-neutral-900">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="min-w-0 truncate text-sm leading-tight font-medium text-gray-900 dark:text-neutral-100">{template.name}</span>
          <IntegrationStatusBadge connected={connected} />
        </div>
        {!configured ? (
          <div className="mt-1 text-xs/5 text-gray-500 dark:text-neutral-400">{template.name} is not configured yet.</div>
        ) : !connected && !isAdmin ? (
          <div className="mt-1 text-xs/5 text-gray-500 dark:text-neutral-400">An admin needs to connect {template.name} first.</div>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {isAdmin ? (
          <button
            type="button"
            className="inline-flex cursor-pointer items-center gap-1 text-xs/4 font-semibold text-gray-600 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:text-neutral-400 dark:hover:text-neutral-100"
            disabled={isAuthorizing || !configured}
            onClick={handleAuthorizeClick}
          >
            <ExternalLinkIcon className="size-3.5 shrink-0" />
            <span>{!configured ? `${template.name} unavailable` : connected ? `Manage ${template.name}` : `Connect ${template.name}`}</span>
          </button>
        ) : null}

        {isAlreadyInstalled ? (
          <div className="flex shrink-0 items-center gap-2">
            <span className="shrink-0 text-xs/4 font-semibold text-gray-500 dark:text-neutral-400">Added</span>
            {isRemoving ? <SpinnerIcon className="size-3.5 shrink-0 animate-spin text-gray-500 dark:text-neutral-400" /> : null}
            <button
              type="button"
              className="shrink-0 cursor-pointer text-xs/4 font-semibold text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-60 dark:text-red-400 dark:hover:text-red-300"
              disabled={removeDisabled || !template.installed_widget_id}
              onClick={handleRemoveClick}
            >
              {isRemoving ? 'Removing' : 'Remove'}
            </button>
          </div>
        ) : (
          <SettingsButton
            type="button"
            variant={connected ? 'primary' : 'secondary'}
            className="shrink-0 rounded-lg px-3 py-2 !text-xs/4"
            disabled={disabled || !connected}
            onClick={handleInstallClick}
          >
            {isInstalling ? 'Adding...' : `Add ${template.name}`}
          </SettingsButton>
        )}
      </div>
    </div>
  );
});

const WidgetGalleryView = ({ channelId, focusSlug }) => {
  const { workspaceId, profile: currentProfile } = useRouteWorkspace();
  const canUseWidgetTemplates = Boolean(currentProfile);
  const isAdmin = currentProfile?.role === 'admin';
  const templateWidgetSignal = useWorkspaceTemplateWidgetSignal(workspaceId);
  const templateLoadWorkspaceIdRef = useRef(null);
  const templateHasLoadedRef = useRef(false);
  const galleryRootRef = useRef(null);
  const appliedFocusSlugRef = useRef(null);
  const [templates, setTemplates] = useState(EMPTY_TEMPLATES);
  const [integrations, setIntegrations] = useState(EMPTY_INTEGRATIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const desktopFocusSignal = useDesktopWindowFocusSignal();
  const clearTemplateError = useCallback((message) => setError(message), []);
  const {
    installingSlug,
    removingSlug,
    installedSlug,
    installTemplate: handleInstallTemplate,
    removeTemplate: handleRemoveTemplate,
    canInstallTemplate,
    canRemoveTemplate,
  } = useWidgetTemplateActions({
    canUseWidgetTemplates,
    workspaceId,
    channelId,
    setTemplates,
    source: 'WidgetGalleryView',
    onError: clearTemplateError,
  });

  useEffect(
    function loadWidgetTemplates() {
      if (!canUseWidgetTemplates || !workspaceId) {
        templateLoadWorkspaceIdRef.current = null;
        templateHasLoadedRef.current = false;
        setTemplates(EMPTY_TEMPLATES);
        setIntegrations(EMPTY_INTEGRATIONS);
        setIsLoading(false);
        setError(null);
        return undefined;
      }

      const abortController = new AbortController();
      const query = new URLSearchParams({ workspace_id: workspaceId });
      const isWorkspaceLoad = templateLoadWorkspaceIdRef.current !== workspaceId;

      if (isWorkspaceLoad) templateHasLoadedRef.current = false;

      const isBackgroundRefresh = !isWorkspaceLoad && templateHasLoadedRef.current;

      templateLoadWorkspaceIdRef.current = workspaceId;

      if (!isBackgroundRefresh) {
        setIsLoading(true);
        setError(null);
      }

      async function fetchTemplates() {
        try {
          const response = await apiGet(`/widget_templates?${query.toString()}`, { signal: abortController.signal });
          const data = await response.json();

          if (!response.ok || !data.ok || !Array.isArray(data.data?.widget_templates)) {
            throw new Error(responseErrorMessage(data, 'Could not load widget gallery.'));
          }

          templateHasLoadedRef.current = true;
          setTemplates(data.data.widget_templates);
          setIntegrations(data.data.integrations || EMPTY_INTEGRATIONS);
        } catch (fetchError) {
          if (fetchError?.name === 'AbortError') return;

          logError('WidgetGalleryView', 'Failed to load widget templates', { error: fetchError });
          if (!isBackgroundRefresh) {
            templateHasLoadedRef.current = false;
            setError(fetchError.message || 'Could not load widget gallery.');
            setTemplates(EMPTY_TEMPLATES);
            setIntegrations(EMPTY_INTEGRATIONS);
          }
        } finally {
          if (!abortController.signal.aborted && !isBackgroundRefresh) setIsLoading(false);
        }
      }

      fetchTemplates();

      return () => abortController.abort();
    },
    [canUseWidgetTemplates, desktopFocusSignal, templateWidgetSignal, workspaceId],
  );

  useEffect(
    function scrollFocusedTemplateIntoView() {
      if (!focusSlug || appliedFocusSlugRef.current === focusSlug) return;

      const element = findTemplateElementBySlug(galleryRootRef.current, focusSlug);
      if (!element) return;

      appliedFocusSlugRef.current = focusSlug;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    [focusSlug, templates],
  );

  const renderTemplateRow = useCallback(
    function renderTemplateRow(template) {
      const provider = template.required_integrations?.[0];

      return (
        <div key={template.slug} data-widget-template-slug={template.slug}>
          {provider ? (
            <WidgetIntegrationRow
              template={template}
              provider={provider}
              status={integrations[provider]}
              disabled={!canInstallTemplate(template, { isLoading })}
              removeDisabled={!canRemoveTemplate(template, { isLoading })}
              isInstalling={installingSlug === template.slug}
              isRemoving={removingSlug === template.slug}
              isAdmin={isAdmin}
              workspaceId={workspaceId}
              onInstall={handleInstallTemplate}
              onRemove={handleRemoveTemplate}
              onError={clearTemplateError}
              desktopFocusSignal={desktopFocusSignal}
            />
          ) : (
            <WidgetTemplateButton
              template={template}
              disabled={!canInstallTemplate(template, { isLoading })}
              removeDisabled={!canRemoveTemplate(template, { isLoading })}
              isInstalling={installingSlug === template.slug}
              isRemoving={removingSlug === template.slug}
              wasInstalled={installedSlug === template.slug}
              onInstall={handleInstallTemplate}
              onRemove={handleRemoveTemplate}
            />
          )}
        </div>
      );
    },
    [
      canInstallTemplate,
      canRemoveTemplate,
      clearTemplateError,
      desktopFocusSignal,
      handleInstallTemplate,
      handleRemoveTemplate,
      installedSlug,
      installingSlug,
      integrations,
      isAdmin,
      isLoading,
      removingSlug,
      workspaceId,
    ],
  );

  return (
    <div ref={galleryRootRef} className="pointer-events-auto flex min-h-full w-full flex-col">
      <div className="flex flex-1 flex-col gap-2 p-3">
        {isLoading ? (
          <div className="flex justify-center py-8" role="status" aria-label="Loading widget gallery">
            <SpinnerIcon className="size-4 animate-spin text-gray-500 dark:text-neutral-400" />
          </div>
        ) : null}
        {!isLoading && templates.length === 0 ? (
          <div className="px-2 py-6 text-sm text-gray-500 dark:text-neutral-400">No widget templates available.</div>
        ) : null}
        {templates.map(renderTemplateRow)}
        {error ? <div className="px-2 pt-2 text-xs leading-5 text-red-600 dark:text-red-400">{error}</div> : null}
      </div>
    </div>
  );
};

export default WidgetGalleryView;
